import React, { useMemo } from "react";
import "dayjs/locale/en-gb";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs, { Dayjs } from "dayjs";
import { Box, Typography } from "@mui/material";
import { DateRange } from "@mui/x-date-pickers-pro";
import { isForecastWeekInPast } from "../Utilities/forecastWeekEditability";

// Type definitions
interface WeekRestrictedDateRangePickerProps {
    buyInDateOptions?: string[];
    value: [string | null, string | null];
    onChange: (value: [string | null, string | null]) => void;
    errorStartDate?: string | null;
    errorEndDate?: string | null;
    startLabel?: string;
    endLabel?: string;
    disablePast?: boolean;
}

const WeekRestrictedDateRangePicker: React.FC<WeekRestrictedDateRangePickerProps> = ({ 
    buyInDateOptions = [], 
    value, 
    onChange, 
    errorStartDate, 
    errorEndDate, 
    startLabel = "Buy In Start", 
    endLabel = "Buy In End",
    disablePast = true
}) => {
    // Create a set of valid date strings for quick lookup
    const validDateStrings = useMemo(() => {
        return new Set(buyInDateOptions);
    }, [buyInDateOptions]);

    // Parse valid dates as dayjs objects
    const validDates = useMemo(() => {
        return buyInDateOptions.map(dateStr => {
            const [day, month, year] = dateStr.split('-').map(Number);
            return dayjs(new Date(year, month - 1, day));
        }).sort((a, b) => a.isBefore(b) ? -1 : 1);
    }, [buyInDateOptions]);

    // Function to check if a date should be disabled
    const shouldDisableDate = (date: Dayjs): boolean => {
        const dateStr = date.format('DD-MM-YYYY');
        const isValidWeek = validDateStrings.has(dateStr);
        const isPastWeek = isForecastWeekInPast(dateStr);
        
        return !isValidWeek || (disablePast && isPastWeek);
    };

    // Function to find the closest valid date
    const getClosestValidDate = (date: Dayjs): string | null => {
        if (!date || validDates.length === 0) return null;
        
        // First check if the date is already valid
        const dateStr = date.format('DD-MM-YYYY');
        if (validDateStrings.has(dateStr)) {
            return dateStr;
        }
        
        // Find the closest valid date
        let closestDate = validDates[0];
        let minDiff = Math.abs(date.diff(closestDate, 'day'));
        
        for (const validDate of validDates) {
            const diff = Math.abs(date.diff(validDate, 'day'));
            if (diff < minDiff) {
                minDiff = diff;
                closestDate = validDate;
            }
        }
        
        return closestDate.format('DD-MM-YYYY');
    };

    // Convert string values back to dayjs objects for the picker
    const pickerValue = useMemo((): DateRange<Dayjs> => {
        if (!value || (!value[0] && !value[1])) {
            return [null, null];
        }
        
        const parseDate = (dateStr: string | null): Dayjs | null => {
            if (!dateStr) return null;
            const [day, month, year] = dateStr.split('-').map(Number);
            return dayjs(new Date(year, month - 1, day));
        };
        
        return [parseDate(value[0]), parseDate(value[1])];
    }, [value]);

    // Handle date range changes
    const handleDateChange = (newValue: DateRange<Dayjs>): void => {
        if (!newValue) {
            onChange([null, null]);
            return;
        }

        const [startDate, endDate] = newValue;
        
        // Convert to nearest valid dates
        const startStr = startDate ? getClosestValidDate(startDate) : null;
        const endStr = endDate ? getClosestValidDate(endDate) : null;
        
        onChange([startStr, endStr]);
    };

    if (!buyInDateOptions.length) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="text.secondary">
                    No buy-in date options available
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DemoContainer components={["DateRangePicker"]}>
                    <DateRangePicker
                        localeText={{ start: startLabel, end: endLabel }}
                        value={pickerValue}
                        onChange={handleDateChange}
                        shouldDisableDate={shouldDisableDate}
                        slotProps={{
                            textField: ({ position }) => ({
                                error: position === "start" ? Boolean(errorStartDate) : Boolean(errorEndDate),
                                helperText: position === "start" ? errorStartDate : errorEndDate,
                            })
                        }}
                    />
                </DemoContainer>
            </LocalizationProvider>
        </Box>
    );
};

export default WeekRestrictedDateRangePicker;
export type { WeekRestrictedDateRangePickerProps }; 